import React from 'react';
import isEmpty from 'lodash/isEmpty';

import { TFunction } from '@lyearn/i18n';
import { Fields, FieldType, useFormFieldValue } from '@/components/Form';
import VisibilitySettingsFormField from '@/components/Form/components/VisibilitySettings';
import { VisibilitySettingsFormFieldProps } from '@/components/Form/components/VisibilitySettings/types';
import useFile from '@/modules/tasks/views/TasksDashboard/hooks/useFile';
import {
  IconSettingsInput,
  IconTypeEnum,
  VisibilitySettingsAccessTypeEnum,
  VisibilitySettingsType,
} from '@/types/schema';

import NameAndEmojiInput from '../../../ProjectEditor/components/NameAndEmojiInput';
import SelectFolder from '../SelectFolder';

export const INITIAL_VALUES: ListEditorFormValues = {
  name: '',
  iconSettings: undefined,
  // folderId: undefined,
  visibilitySettings: {
    accessType: VisibilitySettingsAccessTypeEnum.Open,
  } as VisibilitySettingsType,
};

export const getInitialValues = (
  file?: ReturnType<typeof useFile>['file'],
  initialValues?: Partial<ListEditorFormValues>,
) => {
  if (!file) {
    // May need to do a better merge of initial values and defaults in the future
    return {
      ...INITIAL_VALUES,
      ...initialValues,
    };
  }

  return {
    name: file.name,
    iconSettings: file.iconSettings ?? undefined,
    folderId: undefined,
    // folderId: file.folder?.id ?? undefined, // FIXME: @aryan02420
    visibilitySettings: file.visibilitySettings as VisibilitySettingsType,
  };
};

export type ListEditorFormValues = {
  name: string;
  iconSettings: IconSettingsInput | undefined;
  // folderId: string | undefined;
  visibilitySettings: VisibilitySettingsType;
};

const VISIBILITY_SETTINGS_CONTAINER_OVERRIDES: VisibilitySettingsFormFieldProps['overrides'] = {
  restrictedContent: {
    overrides: {
      label: { variant: 'body-short-02' },
      addButton: { className: 'mt-6' },
    },
  },
};

export const getFormFields = (t: TFunction): Fields<ListEditorFormValues> => {
  return {
    name: {
      name: 'name',
      label: t('Common:Name'),
      type: FieldType.Custom,
      Component: ({ value: nameValue, setFieldValue, error, touched, onChange: onChangeName }) => {
        const iconFieldKey = 'iconSettings';
        const iconValue = (useFormFieldValue(iconFieldKey) as IconSettingsInput)?.value;

        const onChangeIcon = (nextValue: string) => {
          if (nextValue) {
            setFieldValue(
              iconFieldKey,
              {
                type: IconTypeEnum.Emoji,
                value: nextValue,
              },
              true,
            );
          } else {
            setFieldValue(iconFieldKey, undefined, true);
          }
        };

        return (
          <NameAndEmojiInput
            error={touched && error}
            icon={iconValue}
            name={nameValue}
            placeholder={t('Task:List.NamePlaceholder')}
            onChangeIcon={onChangeIcon}
            onChangeName={onChangeName}
          />
        );
      },
    },
    folderId: {
      name: 'folderId',
      label: t('Task:Folder.Folder'),
      required: false,
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        return <SelectFolder folderId={value} onChange={onChange} />;
      },
    },
    visibilitySettings: {
      name: 'visibilitySettings',
      label: t('VisibilitySetting:VisibleTo'),
      type: FieldType.Custom,
      Component: ({ value, onChange }) => (
        <VisibilitySettingsFormField
          overrides={VISIBILITY_SETTINGS_CONTAINER_OVERRIDES}
          type="users_and_groups"
          value={value}
          onChange={onChange}
        />
      ),
    },
  };
};

export const FORM_LAYOUT = ['name', 'visibilitySettings'];

export const validateForm = (t: TFunction) => (values: any) => {
  const errors: any = {};

  if (isEmpty(values.name)) {
    errors.name = t('Task:NameCannotBeEmpty');
  }

  return errors;
};
